import { useEffect, useMemo, useState } from 'react';
import SearchField from 'src/shared/components/form/searchField';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { MemberAutogiroStatus } from '../models/memberAutogiroStatus';
import { MemberAutogiroStatusDataGrid } from '../components/memberAutogiroStatusDataGrid';

export const MemberAutogiroStatusOverviewPage = () => {
  const isMounted = useIsMounted();
  const { t } = useTranslation();
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const [memberAutogiroStatusFilter, setMemberAutogiroStatusFilter] = useLocalStorage(LocalStorageKeys.MemberAutogiroStatusFilter, {
    sortCol: 'created',
    sortDir: 'desc',
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
  } as MemberAutogiroStatus);

  const searchFilter: MemberAutogiroStatus = useMemo(() => {
    return {
      filter: memberAutogiroStatusFilter?.filter,
      pageSize: memberAutogiroStatusFilter?.pageSize,
      pageIndex: memberAutogiroStatusFilter?.pageIndex,
      sortCol: memberAutogiroStatusFilter?.sortCol,
      sortDir: memberAutogiroStatusFilter?.sortDir,
    } as MemberAutogiroStatus;
  }, [
    memberAutogiroStatusFilter?.filter,
    memberAutogiroStatusFilter?.pageIndex,
    memberAutogiroStatusFilter?.pageSize,
    memberAutogiroStatusFilter?.sortCol,
    memberAutogiroStatusFilter?.sortDir,
  ]);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setMemberAutogiroStatusFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setMemberAutogiroStatusFilter((prev) => ({ ...prev, ...{ filter: tempSearchString, pageIndex: 0 } }));
      } else {
        setMemberAutogiroStatusFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
    }
  }, [isMounted, searchInitialized, setMemberAutogiroStatusFilter, tempSearchString]);

  return (
    <>
      <Alert severity='info' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {t('invoice:autogiro.alertInfoText')}
      </Alert>
      <PageFilterSection
        leftContent={
          <SearchField
            initialValue={searchFilter.filter || ''}
            onSearch={(newSearchString) => {
              setSearchInitialized(true);
              setTempSearchString(newSearchString);
            }}
          />
        }
      />
      <MemberAutogiroStatusDataGrid
        loadingCallback={(x) => ConsoleHelper.log(x)}
        searchFilter={searchFilter}
        searchFilterCallback={(newFilter) =>
          setMemberAutogiroStatusFilter((prev) => ({
            ...prev,
            ...newFilter,
          }))
        }
      />
    </>
  );
};
